import React, { useCallback, useEffect, useState } from 'react';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { faMemoPad } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { debounce } from 'lodash';

import { getMailersHeaderPreview, updateMailersHeader } from 'bundles/connect/helpers/prospect_api';
import type { MailersHeader } from 'bundles/connect/types';
import { Button, Spinner } from 'react-bootstrap-5';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type MailersHeaderPreviewProps = {
  initialHeader: MailersHeader;
  edit?: boolean;
  setShow?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MailersHeaderPreview({ initialHeader, edit, setShow }: MailersHeaderPreviewProps) {
  const [header, setHeader] = useState<MailersHeader>(initialHeader);
  const [loading, setLoading] = useState(false);

  const hiddenContentStyle: React.CSSProperties = header.hidden ? { opacity: 0.2, userSelect: 'none' } : {};

  const debouncedRefresh = useCallback(debounce((header) => {
    refreshPreview(header);
  }, 1000), []);

  const refreshPreview = (header: MailersHeader) => {
    setLoading(true);
    getMailersHeaderPreview(
      { html_left: header.htmlLeft, html_right: header.htmlRight }
    ).then((response) => {
      setHeader({ ...response, logoUrl: header.logoUrl });
    }).finally(() => {
      setLoading(false);
    });
  };

  const hideHeader = async (hide: boolean) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('id', String(header.id));
    formData.append('hidden', String(hide));
    try {
      const response: MailersHeader = await updateMailersHeader(formData);
      setHeader({ ...response });
      const displayMessage = response.hidden ? 'Letterhead hidden' : 'Letterhead will show';
      displayToast(displayMessage, 'success');
    } catch {
      displayToast('Something went wrong, please try again', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    debouncedRefresh(initialHeader);
  }, [initialHeader]);

  const showPreview = !header.hidden && (header.htmlLeft || header.htmlRight || header.logoUrl);
  return (
    <div className="mt-2">
      {!edit && (
        <div className="d-flex flex-row align-items-center mb-3">
          <strong className="me-3">Preview</strong>
          {loading && <Spinner animation="border" size="sm" variant="secondary" />}
        </div>
      )}
      {(showPreview || edit) &&
        <div
          className={classNames({ border: !edit }, 'rounded p-2 bg-white d-flex')}
          style={{ border: `${edit ? '2px dashed #1D357E' : ''}` }}
        >
          <div className="me-auto" style={hiddenContentStyle}>
            {header.logoUrl && <img src={header.logoUrl} style={{ maxHeight: '200px', maxWidth: '200px' }} />}
            <div dangerouslySetInnerHTML={{ __html: header.htmlLeft }} />
          </div>
          {edit &&
            <div className="d-flex gap-2 align-items-center justify-content-center ">
              <Button
                variant="secondary"
                onClick={() => setShow && setShow(true)}
              >
                <FontAwesomeIcon icon={faMemoPad} />
                Edit letterhead
              </Button>
              <Button
                disabled={loading}
                variant="outline"
                onClick={() => hideHeader(!header.hidden)}
              >
                <FontAwesomeIcon icon={header.hidden ? faEye : faEyeSlash} />
                {header.hidden ? 'Show' : 'Hide'} letterhead
              </Button>
            </div>
          }
          <div dangerouslySetInnerHTML={{ __html: header.htmlRight }} style={hiddenContentStyle}></div>
        </div>}
    </div>
  );
}
